/**
 * DBCalculatorFull — KTBL 6-Block Full Cost Accordion (Sprint 54)
 *
 * Displays the full KTBL cost structure per crop:
 * Block 1: Marktleistungen
 * Block 2: Direktkosten
 * → Direktkostenfreie Leistung
 * Block 3: Arbeitserledigungskosten
 * → DAKL
 * Block 4: Gebäudekosten
 * Block 5: Flächenkosten
 * Block 6: Sonstige Gemeinkosten
 * → Vollkosten | Gewinn/Verlust | Break-Even Preis & Ertrag
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../lib/apiClient';
import SourceBadge from './SourceBadge';

function LineItem({ label, item, onEdit, currency }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  if (!item) return null;

  const handleSave = () => {
    const val = parseFloat(draft);
    if (!isNaN(val) && onEdit) onEdit(val);
    setEditing(false);
  };

  return (
    <div className="flex items-center justify-between py-1 px-1 rounded hover:bg-[var(--aif-bg-elevated)] group">
      <span className="text-xs text-[var(--aif-text-secondary)] flex-1">{label}</span>
      <div className="flex items-center gap-1 text-right">
        {editing ? (
          <div className="flex items-center gap-1">
            <input
              type="number"
              value={draft}
              onChange={e => setDraft(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') setEditing(false); }}
              onBlur={handleSave}
              autoFocus
              className="w-20 text-xs text-right bg-[var(--aif-bg-input)] border border-[var(--aif-color-primary)] rounded px-1 py-0.5 text-[var(--aif-text-primary)] outline-none"
            />
          </div>
        ) : (
          <>
            <SourceBadge source={item.source} variant="compact" className="ml-1" />
            <span
              className={`text-xs font-medium text-[var(--aif-text-primary)] cursor-pointer hover:text-[var(--aif-color-primary)] min-w-[60px] text-right`}
              onClick={() => { setDraft(String(item.value)); setEditing(true); }}
            >
              {item.value?.toLocaleString('de-DE')} <span className="text-[9px] text-[var(--aif-text-muted)]">{item.unit}</span>
            </span>
          </>
        )}
        {item.source !== 'reference' && !editing && (
          <span className="text-[9px] text-[var(--aif-text-muted)] opacity-0 group-hover:opacity-100">({item.reference?.toLocaleString('de-DE')} Ref)</span>
        )}
      </div>
    </div>
  );
}

function BlockSection({ number, title, children, total, currency, accent, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="border border-[var(--aif-border-default)] rounded-xl overflow-hidden">
      <button
        className="w-full flex items-center justify-between px-3 py-2.5 bg-[var(--aif-bg-surface)] hover:bg-[var(--aif-bg-elevated)] transition-colors"
        onClick={() => setOpen(o => !o)}
      >
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold text-[var(--aif-text-muted)] bg-[var(--aif-bg-elevated)] rounded-full w-5 h-5 flex items-center justify-center border border-[var(--aif-border-default)]">
            {number}
          </span>
          <span className="text-xs font-semibold text-[var(--aif-text-primary)]">{title}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-sm font-bold ${accent || 'text-[var(--aif-text-primary)]'}`}>
            {total?.toLocaleString('de-DE')} {currency}
          </span>
          <svg className={`w-3.5 h-3.5 text-[var(--aif-text-muted)] transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>
      {open && (
        <div className="px-3 py-2 bg-[var(--aif-bg-page)] space-y-0.5">
          {children}
        </div>
      )}
    </div>
  );
}

function SubtotalBar({ label, value, currency, variant = 'neutral' }) {
  const colors = {
    neutral: 'border-[var(--aif-border-default)] bg-[var(--aif-bg-elevated)]',
    positive: 'border-green-500/30 bg-green-500/10',
    negative: 'border-red-500/30 bg-red-500/10',
    primary: 'border-[var(--aif-color-primary)]/30 bg-[var(--aif-color-primary)]/10',
  };
  const textColors = {
    neutral: 'text-[var(--aif-text-primary)]',
    positive: 'text-green-400',
    negative: 'text-red-400',
    primary: 'text-[var(--aif-color-primary)]',
  };
  return (
    <div className={`flex items-center justify-between px-3 py-2 rounded-lg border ${colors[variant]} my-1`}>
      <span className="text-xs font-semibold text-[var(--aif-text-secondary)]">{label}</span>
      <span className={`text-sm font-bold ${textColors[variant]}`}>
        {value >= 0 ? '' : '−'}{Math.abs(value)?.toLocaleString('de-DE')} {currency}
      </span>
    </div>
  );
}

export default function DBCalculatorFull({ token, cropCode, cropName, currency = 'EUR' }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    if (!token || !cropCode) return;
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`/api/v1/aif/finance/contribution-margin/full/${cropCode}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) {
        const json = await r.json();
        setData(json);
      } else {
        setError('Keine Daten verfügbar');
      }
    } catch {
      setError('Verbindungsfehler');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [token, cropCode]);

  const handleEdit = async (blockKey, fieldKey, newValue) => {
    // Map block+field to the override key name
    const keyMap = {
      'block1.yield_t_ha': 'yield_t_ha',
      'block1.price_per_t': 'market_price_t',
      'block1.nebenprodukt': 'straw_revenue',
      'block1.subsidies': 'subsidy_per_ha',
      'block2.seed': 'seed',
      'block2.fertilizer': 'fertilizer',
      'block2.plant_protection': 'plant_protection',
      'block2.drying': 'drying',
      'block2.insurance_crop': 'insurance_crop',
      'block2.misc': 'misc_variable',
      'block3.machinery_variable': 'machinery_variable',
      'block3.machinery_fixed': 'machinery_fixed',
      'block3.contractor': 'contractor',
      'block3.labor_costs': 'labor_costs',
      'block4.building_costs': 'building_costs',
      'block5.land_lease': 'land_lease',
      'block6.overhead_costs': 'overhead_costs',
    };
    const overrideKey = keyMap[`${blockKey}.${fieldKey}`];
    if (!overrideKey) return;

    // Map override key to the PATCH endpoint's field name
    const apiFieldMap = {
      yield_t_ha: 'yield_qty',
      market_price_t: 'price_per_unit',
      subsidy_per_ha: 'subsidy_per_ha',
      seed: 'seed_cost',
      fertilizer: 'fertilizer_cost',
      plant_protection: 'ppp_cost',
      drying: 'drying_cost',
      insurance_crop: 'insurance_cost',
      misc_variable: 'misc_cost',
      machinery_variable: 'machinery_cost',
      machinery_fixed: 'fixed_direct_cost',
      contractor: 'contractor_cost',
      land_lease: 'land_lease_cost',
      straw_revenue: 'straw_revenue',
      labor_costs: 'labor_costs',
      building_costs: 'building_costs',
      overhead_costs: 'overhead_costs',
    };
    const apiField = apiFieldMap[overrideKey];
    if (!apiField) return;

    try {
      await apiClient.patch(`/api/v1/aif/finance/overrides/${cropCode}`, { [apiField]: newValue }, { queueOnOffline: true }); // TODO(5b): confirm 3WM — finance override PATCH
      await load();
    } catch { /* ApiError */ }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="w-5 h-5 rounded-full border-2 border-[var(--aif-color-primary)] border-t-transparent animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return <div className="text-xs text-[var(--aif-text-muted)] p-4 text-center">{error || 'Keine Daten'}</div>;
  }

  const pf = data.per_ha;
  const c = pf.currency || currency;
  const b1 = pf.block1 || {};
  const b2 = pf.block2 || {};
  const b3 = pf.block3 || {};
  const b4 = pf.block4 || {};
  const b5 = pf.block5 || {};
  const b6 = pf.block6 || {};
  const gewinn = pf.gewinn_verlust ?? 0;
  const isProfit = gewinn >= 0;

  return (
    <div className="space-y-2 pb-4">
      {/* Header */}
      <div className="text-center py-2">
        <div className="text-xs text-[var(--aif-text-muted)]">{cropName} — KTBL Vollkostenrechnung</div>
        <div className={`text-2xl font-bold mt-1 ${isProfit ? 'text-green-400' : 'text-red-400'}`}>
          {isProfit ? '+' : ''}{gewinn?.toLocaleString('de-DE')} {c}/ha
        </div>
        <div className="text-xs text-[var(--aif-text-muted)]">
          {t('finance.profitLoss')} | Vollkosten: {pf.vollkosten?.toLocaleString('de-DE')} {c}/ha
        </div>
      </div>

      {/* Block 1: Leistungen */}
      <BlockSection
        number="1"
        title={t('finance.block1Title')}
        total={b1.total}
        currency={c}
        accent="text-green-400"
        defaultOpen
      >
        <LineItem
          label={`${t('finance.hauptprodukt')} (${b1.yield_t_ha?.value?.toLocaleString('de-DE')} t/ha × ${b1.price_per_t?.value?.toLocaleString('de-DE')} ${c}/t)`}
          item={b1.hauptprodukt}
          onEdit={v => handleEdit('block1', 'hauptprodukt', v)}
          currency={c}
        />
        <LineItem label={t('finance.nebenprodukt')} item={b1.nebenprodukt} onEdit={v => handleEdit('block1', 'nebenprodukt', v)} currency={c} />
        <LineItem label={t('finance.subsidyPayment')} item={b1.subsidies} onEdit={v => handleEdit('block1', 'subsidies', v)} currency={c} />
        <div className="flex items-center justify-between pt-1 border-t border-[var(--aif-border-default)] mt-1">
          <span className="text-[10px] font-bold text-[var(--aif-text-muted)]">= {t('finance.sumLeistungen')}</span>
          <span className="text-xs font-bold text-green-400">{b1.total?.toLocaleString('de-DE')} {c}/ha</span>
        </div>
      </BlockSection>

      {/* Block 2: Direktkosten */}
      <BlockSection
        number="2"
        title={t('finance.block2Title')}
        total={b2.total}
        currency={c}
        accent="text-red-400"
      >
        <LineItem label={t('finance.seed')} item={b2.seed} onEdit={v => handleEdit('block2', 'seed', v)} currency={c} />
        <LineItem label={t('finance.fertilizer')} item={b2.fertilizer} onEdit={v => handleEdit('block2', 'fertilizer', v)} currency={c} />
        <LineItem label={t('finance.ppp')} item={b2.plant_protection} onEdit={v => handleEdit('block2', 'plant_protection', v)} currency={c} />
        <LineItem label={t('finance.drying')} item={b2.drying} onEdit={v => handleEdit('block2', 'drying', v)} currency={c} />
        <LineItem label={t('finance.insurance')} item={b2.insurance_crop} onEdit={v => handleEdit('block2', 'insurance_crop', v)} currency={c} />
        <LineItem label={t('finance.misc')} item={b2.misc} onEdit={v => handleEdit('block2', 'misc', v)} currency={c} />
        <div className="flex items-center justify-between pt-1 border-t border-[var(--aif-border-default)] mt-1">
          <span className="text-[10px] font-bold text-[var(--aif-text-muted)]">= Direktkosten gesamt</span>
          <span className="text-xs font-bold text-red-400">{b2.total?.toLocaleString('de-DE')} {c}/ha</span>
        </div>
      </BlockSection>

      {/* DKFL */}
      <SubtotalBar label={`→ ${t('finance.direktkostenfreiLeistung')}`} value={pf.direktkostenfreie_leistung} currency={`${c}/ha`} variant="primary" />

      {/* Block 3: Arbeitserledigung */}
      <BlockSection
        number="3"
        title={t('finance.block3Title')}
        total={b3.total}
        currency={c}
        accent="text-orange-400"
      >
        <LineItem label={t('finance.machineryVariable')} item={b3.machinery_variable} onEdit={v => handleEdit('block3', 'machinery_variable', v)} currency={c} />
        <LineItem label={t('finance.machineryFixed')} item={b3.machinery_fixed} onEdit={v => handleEdit('block3', 'machinery_fixed', v)} currency={c} />
        <LineItem label={t('finance.contractor')} item={b3.contractor} onEdit={v => handleEdit('block3', 'contractor', v)} currency={c} />
        <LineItem label={t('finance.laborCosts')} item={b3.labor_costs} onEdit={v => handleEdit('block3', 'labor_costs', v)} currency={c} />
        <div className="flex items-center justify-between pt-1 border-t border-[var(--aif-border-default)] mt-1">
          <span className="text-[10px] font-bold text-[var(--aif-text-muted)]">= {t('finance.sumArbeit')}</span>
          <span className="text-xs font-bold text-orange-400">{b3.total?.toLocaleString('de-DE')} {c}/ha</span>
        </div>
      </BlockSection>

      {/* DAKL */}
      <SubtotalBar label={`→ ${t('finance.dakl')}`} value={pf.dakl} currency={`${c}/ha`} variant={pf.dakl >= 0 ? 'positive' : 'negative'} />

      {/* Block 4: Gebäude */}
      <BlockSection
        number="4"
        title={t('finance.block4Title')}
        total={b4.total}
        currency={c}
      >
        <LineItem label={t('finance.buildingCosts')} item={b4.building_costs} onEdit={v => handleEdit('block4', 'building_costs', v)} currency={c} />
      </BlockSection>

      {/* Block 5: Fläche */}
      <BlockSection
        number="5"
        title={t('finance.block5Title')}
        total={b5.total}
        currency={c}
      >
        <LineItem label={t('finance.landLease')} item={b5.land_lease} onEdit={v => handleEdit('block5', 'land_lease', v)} currency={c} />
      </BlockSection>

      {/* Block 6: Gemeinkosten */}
      <BlockSection
        number="6"
        title={t('finance.block6Title')}
        total={b6.total}
        currency={c}
      >
        <LineItem label={t('finance.overheadCosts')} item={b6.overhead_costs} onEdit={v => handleEdit('block6', 'overhead_costs', v)} currency={c} />
      </BlockSection>

      {/* Summary */}
      <div className="rounded-xl border-2 border-[var(--aif-color-primary)]/40 bg-[var(--aif-color-primary)]/5 p-3 space-y-2 mt-2">
        <div className="flex justify-between items-center">
          <span className="text-xs font-semibold text-[var(--aif-text-secondary)]">{t('finance.fullCostPerHa')}</span>
          <span className="text-sm font-bold text-[var(--aif-text-primary)]">{pf.vollkosten?.toLocaleString('de-DE')} {c}/ha</span>
        </div>
        <div className="flex justify-between items-center border-t border-[var(--aif-border-default)] pt-2">
          <span className="text-xs font-semibold text-[var(--aif-text-secondary)]">{t('finance.profitLoss')}</span>
          <span className={`text-lg font-bold ${isProfit ? 'text-green-400' : 'text-red-400'}`}>
            {isProfit ? '+' : ''}{gewinn?.toLocaleString('de-DE')} {c}/ha
          </span>
        </div>
      </div>

      {/* Break-Even Box */}
      <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 space-y-1.5">
        <div className="text-[10px] font-bold text-amber-400 uppercase tracking-wide">Break-Even Analyse</div>
        {pf.break_even_price != null && (
          <div className="flex justify-between items-center">
            <span className="text-xs text-[var(--aif-text-secondary)]">{t('finance.breakEvenPrice')}</span>
            <span className="text-sm font-bold text-amber-400">{pf.break_even_price?.toLocaleString('de-DE')} {c}/t</span>
          </div>
        )}
        {pf.break_even_yield != null && (
          <div className="flex justify-between items-center">
            <span className="text-xs text-[var(--aif-text-secondary)]">{t('finance.breakEvenYield')}</span>
            <span className="text-sm font-bold text-amber-400">{pf.break_even_yield?.toLocaleString('de-DE')} t/ha</span>
          </div>
        )}
        {pf.break_even_price != null && b1.price_per_t?.value != null && (
          <div className="text-[10px] text-[var(--aif-text-muted)] mt-1 italic">
            {t('finance.ktblHint', {
              price: b1.price_per_t.value?.toLocaleString('de-DE'),
              yield: pf.break_even_yield?.toLocaleString('de-DE'),
            })}
          </div>
        )}
      </div>

      {/* Data quality */}
      <div className="text-[10px] text-[var(--aif-text-muted)] text-center pt-1">
        {pf.user_data_pct > 0
          ? t('finance.userDataPct', { pct: pf.user_data_pct })
          : t('finance.referenceDataSource')}
      </div>
    </div>
  );
}
